import React, { useCallback, useMemo, useState } from 'react';

import { getEditorWorkflowRoute } from '@aline/common';
import { Collapse, IconButton, Typography } from '@mui/material';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { WorkflowInboxWorkflow } from 'src/components/dashboard';
import { WorkflowInboxFilter } from 'src/components/dashboard/components/workflows/WorkflowsInbox';
import {
  getIsCommentInDocAndUnresolved,
  useGetIsInboxComment,
  useGetIsInboxUnusedAction,
} from 'src/components/dashboard/components/workflows/WorkflowTasks';
import { SanityCheckModal } from 'src/components/send-for-signature/components/sanity-check-modal';
import type { OpenDialogType } from 'src/components/send-for-signature/components/sanity-check-modal';
import { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails, useDispatchNotification } from 'src/helpers';
import { useGetUnassignedWorkflowPermissions } from 'src/hooks/assignment';
import { useGetWorkflowInboxWorkflow } from 'src/hooks/workflow/workflow';
import { alineLinkButtonStylesWithUnderline } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const INBOX_FILTER = WorkflowInboxFilter.All;

const useStyles = makeStyles()(theme => ({
  collapseContent: {
    paddingTop: theme.spacing(0.5),
  },
  docLink: {
    ...alineLinkButtonStylesWithUnderline,
    fontSize: 12,
  },
  fixAction: {
    ...alineLinkButtonStylesWithUnderline,
    fontSize: 12,
    marginLeft: theme.spacing(0.5),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.25),
    width: '100%',
  },
  headerTitle: {
    color: theme.appColors.iconsType,
    flex: 1,
    fontSize: 12,
    fontWeight: 700,
    minWidth: 0,
  },
  icon: {
    color: theme.palette.error.main,
  },
  issueRow: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  issueText: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    fontWeight: 600,
  },
  root: {
    backgroundColor: 'rgba(255, 160, 72, 0.1)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
  },
}));

export function SigningBlockingIssuesAlert({ workflowId }: { workflowId: UUID }) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { loading, workflow } = useGetWorkflowInboxWorkflow(workflowId);
  const unusedActionsInbox = useInboxUnusedActions(workflow);
  const [openDialog, setOpenDialog] = useState<OpenDialogType | null>(null);
  const isReadyForSignature = workflow?.state === Apollo.WorkflowState.ReadyForSignature;

  const { loading: unassignedLoading, unassignedWorkflowPermissions } =
    useGetUnassignedWorkflowPermissions({ workflowId });
  const filesWithUnresolvedComments = useFilesWithBlockingUnresolvedComments(workflow);
  const hasUnassignedRoles = !unassignedLoading && unassignedWorkflowPermissions.length > 0;
  const hasBlockingIssues = Boolean(
    workflow?.missingActions ||
    unusedActionsInbox?.length > 0 ||
    filesWithUnresolvedComments.length > 0 ||
    hasUnassignedRoles
  );
  const dispatchNoFile = useDispatchNotification({
    message: 'No workflow file found.',
    variant: 'error',
  });

  const handleSanityModalClose = useCallback(() => {
    setOpenDialog(null);
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const onClickFixMissingActionsFromDialog = useCallback(() => {
    const firstFile = workflow?.files.at(0);
    if (!firstFile) return dispatchNoFile();

    navigate(getEditorWorkflowRoute(firstFile.document.id, workflowId));
  }, [dispatchNoFile, navigate, workflow?.files, workflowId]);

  if (
    loading ||
    !workflow ||
    workflow.state === Apollo.WorkflowState.Template ||
    !isReadyForSignature ||
    !hasBlockingIssues
  )
    return null;

  return (
    <>
      <div className={classes.root} data-cy="signing-blocking-issues-alert">
        <div className={classes.header}>
          <Info aria-hidden className={classes.icon} size={16} />
          <Typography className={classes.headerTitle} component="div">
            {getBlockingAlertTitle(workflow.state)}
          </Typography>
          <IconButton
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            edge="end"
            size="small"
            onClick={toggleExpanded}
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </IconButton>
        </div>
        <Collapse in={expanded}>
          <div className={classes.collapseContent}>
            {workflow.missingActions && (
              <BlockingIssueRow
                classes={classes}
                issueText="There are unassigned fields in some workflow documents."
                onFix={() => setOpenDialog('missing-actions')}
              />
            )}

            {unusedActionsInbox.map(action => {
              const { primary: signerDisplayName } = getAssigneeDisplayDetails(
                action.assignee,
                action.assigneeRole
              );
              return (
                <BlockingIssueRow
                  key={action.id}
                  classes={classes}
                  issueText={`Signer "${signerDisplayName}" has no signatures in any document`}
                  onFix={() => setOpenDialog('unused-actions')}
                />
              );
            })}

            {filesWithUnresolvedComments.length > 0 && (
              <div className={classes.issueRow}>
                <Typography className={classes.issueText} component="div">
                  Unresolved comments found in{' '}
                  {filesWithUnresolvedComments.map((wf, index) => (
                    <React.Fragment key={wf.id}>
                      {index > 0 ? ', ' : ''}
                      <DocumentNameLink
                        className={classes.docLink}
                        documentId={wf.document.id}
                        name={wf.document.name}
                        workflowId={workflowId}
                      />
                    </React.Fragment>
                  ))}
                </Typography>
              </div>
            )}

            {hasUnassignedRoles && (
              <BlockingIssueRow
                classes={classes}
                issueText="Some workflow roles are not assigned to collaborators."
                onFix={() => setOpenDialog('unassigned-roles')}
              />
            )}
          </div>
        </Collapse>
      </div>
      <SanityCheckModal
        openDialogTuple={[openDialog, setOpenDialog]}
        workflowId={workflowId}
        onClickFixMissingActions={onClickFixMissingActionsFromDialog}
        onClose={handleSanityModalClose}
      />
    </>
  );
}

function BlockingIssueFixAction({
  className,
  onClick,
}: {
  className: string;
  onClick: () => void;
}) {
  return (
    <Typography className={className} onClick={onClick}>
      Fix
    </Typography>
  );
}

type BlockingIssueRowClasses = Pick<
  ReturnType<typeof useStyles>['classes'],
  'fixAction' | 'issueRow' | 'issueText'
>;

function BlockingIssueRow({
  classes: rowClasses,
  issueText,
  onFix,
}: {
  classes: BlockingIssueRowClasses;
  issueText: string;
  onFix: () => void;
}) {
  return (
    <div className={rowClasses.issueRow}>
      <Typography className={rowClasses.issueText} component="span">
        {issueText}
      </Typography>
      <BlockingIssueFixAction className={rowClasses.fixAction} onClick={onFix} />
    </div>
  );
}

function DocumentNameLink({
  className,
  documentId,
  name,
  workflowId,
}: {
  className: string;
  documentId: UUID;
  name: string;
  workflowId: UUID;
}) {
  return (
    <RouterLink className={className} to={getEditorWorkflowRoute(documentId, workflowId)}>
      {name}
    </RouterLink>
  );
}

function getBlockingAlertTitle(state: Apollo.WorkflowState) {
  if (state === Apollo.WorkflowState.Template) {
    return 'There are blocking issues to resolve';
  }

  return 'There are errors blocking signing';
}

function useFilesWithBlockingUnresolvedComments(workflow: WorkflowInboxWorkflow | null) {
  const { getIsInboxComment } = useGetIsInboxComment();

  return useMemo(() => {
    if (!workflow) return [];

    if (workflow.state === Apollo.WorkflowState.Template) {
      return workflow.files.filter(file =>
        file.document.comments.some(comment => getIsCommentInDocAndUnresolved(comment))
      );
    }

    return workflow.files.filter(file =>
      file.document.comments.some(comment => getIsInboxComment(comment, workflow, INBOX_FILTER))
    );
  }, [getIsInboxComment, workflow]);
}

function useInboxUnusedActions(workflow: WorkflowInboxWorkflow | null) {
  const { getIsInboxUnusedAction } = useGetIsInboxUnusedAction();

  return useMemo(() => {
    if (!workflow) return [];

    return workflow.unusedActions.filter(action => getIsInboxUnusedAction(action, INBOX_FILTER));
  }, [getIsInboxUnusedAction, workflow]);
}
